import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { playNotificationSound } from '../../utils/SoundUtils';
import './Toast.css';

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

let toastIdCounter = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const showToast = useCallback((message, type = 'info', { duration, sound = true } = {}) => {
    const id = ++toastIdCounter;
    const defaultDuration = type === 'success' ? 4000 : type === 'error' ? 6000 : 5000;
    const toastDuration = duration || defaultDuration;

    if (sound) {
      playNotificationSound(type);
    }

    setToasts(prev => [...prev, { id, message, type }]);

    timersRef.current[id] = setTimeout(() => {
      removeToast(id);
    }, toastDuration);

    return id;
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      <div className="toast-container" role="status" aria-live="polite">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            <span className="toast-icon">
              {toast.type === 'success' && <i className="fas fa-check-circle" />}
              {toast.type === 'error' && <i className="fas fa-exclamation-circle" />}
              {toast.type === 'warning' && <i className="fas fa-exclamation-triangle" />}
              {toast.type === 'info' && <i className="fas fa-info-circle" />}
            </span>
            <span className="toast-message">{toast.message}</span>
            <button className="toast-close" onClick={() => removeToast(toast.id)} aria-label="Dismiss notification">
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;